import { Component, computed, Injector, inject, input } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable, EMPTY, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { ChatPollingService } from 'src/app/features/chat/services/chat-polling.service';
import { IMessageDto, messageDto } from 'src/assets/common/models/message-dto.g';
import { IMessageListDto } from 'src/assets/common/models/message-list-dto.g';
import { IMessageListData, messageListData } from '../../models/message-list-data';
import { IMessageSendDialogData, MessageSendDialogComponent } from '../message-send-dialog/message-send-dialog.component';
import { MessageListService } from '../../services/message-list.service';
import { MessagePageService } from '../../services/message-page.service';
import { AppDatePipe } from 'src/app/infrastructure/pipes/app-date.pipe';
import { ExpansionList_ToolComponent } from 'src/app/features/shared/lists/expansion-list/container/expansion-list-tool/expansion-list.component';
import { FilterService } from 'src/app/features/shared/filter/filter.service';
import { ILevelGeneralListData } from 'src/app/infrastructure/models/level-general-list-data';
import { LIST_KIND_NAME, _ListService } from 'src/app/infrastructure/services/list-services/_list.service';
import { PagerService } from 'src/app/features/shared/pager/pager.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { TabDataService } from 'src/app/features/shared/central-page/services/tab-data.service';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { TabListStorageService } from 'src/app/features/shared/storage/services/storage-services/tab-list-storage.service';
import { _NavigationList_Base_FeatureComponent } from 'src/app/infrastructure/components/tablist-components/_navigation-list-base-feature.component';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { claimPage } from 'src/app/infrastructure/models/claim-page';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { showAnimation } from 'src/app/infrastructure/animations/show-animation';
import { tabName } from 'src/app/infrastructure/services/page-services/models/tab-name';

// Waar de lijst staat; bepaalt de request, de claimpagina en of er verzonden kan worden.
export const messageListContext = {
    project: 'project',
    task: 'task',
    domain: 'domain',
    function: 'function',
    mine: 'mine',
} as const;
export type TMessageListContext = (typeof messageListContext)[keyof typeof messageListContext];

// Berichtenlijst, vier plekken (gekozen met de input context, niet via de actieve tab):
//   - Project › messages (listFromProject) en Domain › messages (listFromDomain): de berichten van die context die je
//     mag lezen; toevoegknop = verzenden (dialoog, alleen met <tab>/BtnListAdd/Show = beheer). Project toont alleen
//     berichten zonder taak.
//   - Taak › messages (listFromTask, key = de taak op de pagina): de berichten van die taak; verzenden mag wie de taak
//     leest (taakleden en toezicht, fase 3c), aan alle taakleden of losse taakleden.
//   - Function › messages (listFromFunction): het berichtendossier van de functie.
//   - Home › messages (mine): ontvangen berichten over alle domains; regel 1 noemt dan ook domain en project.
// Rij: (context ·) tijd · afzender; onderwerp; doelgroep. Status: ongelezen, te bevestigen, en voor afzender/beheer
// hoeveel ontvangers het gelezen/bevestigd hebben. Gelezen (en bevestigd) = gedimd.
@Component({
    selector: 'app-message-tablist',
    templateUrl: './message-tablist.component.html',
    styleUrls: [
        '../../../shared/central-page/central-page.scss',
        '../../../shared/central-page/container/tablist/tablist.scss',
        '../../../shared/lists/title-subline-row.scss',
        './message-tablist.component.scss',
    ],
    standalone: true,
    imports: [
        AppDatePipe,
        ExpansionList_ToolComponent,
        SharedModule,
    ],
    providers: [
        {provide: LIST_KIND_NAME, useValue: tabName.projectMessages},
        TabListStorageService,
        TabDataService,
        PagerService,
        {provide: FilterService, useExisting: PagerService},

        {provide: _PageService, useExisting: MessagePageService},
        {provide: _ListService, useExisting: MessageListService},
    ],
    animations: [
        showAnimation
    ]
})
export class MessageTablistComponent
    extends _NavigationList_Base_FeatureComponent<IMessageListDto, IMessageListData>
{
    private readonly __apiService = injectRequired(ApiService);
    private readonly __chatPollingService = injectRequired(ChatPollingService);
    private readonly __injector = inject(Injector);
    private readonly __matDialog = injectRequired(MatDialog);
    private readonly __messagePageService = injectRequired(MessagePageService);

    protected override readonly _listService = injectRequired(_ListService);
    protected override readonly _pageService = injectRequired(_PageService);
    protected override readonly _pagerService = injectRequired(PagerService);
    protected override readonly _tabDataService = injectRequired(TabDataService);
    protected override readonly _tabListService = injectRequired(TabListService);

    override input_btnAddData$$ = this._input_btnAddData$$;
    override input_btnDeleteData$$ = this._input_btnDeleteData$$;
    override input_buttonsHide$$ = this._input_buttonsHide$$;
    override input_claimPageList$$ = this._input_claimPageList$$;
    override input_noClick$$ = this._input_noClick$$;
    override input_pageSize$$ = this._input_pageSize$$;

    override out_onClick = this._out_onClick;
    override out_onPageLoaded = this._out_onPageLoaded;
    override out_onSave = this._out_onSave;

    override btnAddData$$ = this._input_btnAddData$$;
    override btnDeleteData$$ = this._input_btnDeleteData$$;
    override buttonsHide$$ = this._input_buttonsHide$$;
    override httpParams$$ = this._computed_httpParams$$;
    override list$$ = this._computed_list$$;
    override noClick$$ = this._input_noClick$$;
    override pageSize$$ = this._input_pageSize$$;
    override requestName$$ = this._computed_requestName$$;
    override tabIndex$$ = this._computed_tabIndex$$;

    readonly context$$ = input<TMessageListContext>(messageListContext.project, { alias: 'context' });
    override claimPageList$$ = computed(() => {
        switch (this.context$$()) {
            case messageListContext.domain: return claimPage.domainMessages;
            case messageListContext.function: return claimPage.functionMessages;
            case messageListContext.mine: return claimPage.homeMessages;
            case messageListContext.task: return claimPage.taskMessages;
            default: return claimPage.projectMessages;
        }
    });
    // Regel 1: (domain › project ·) tijd · afzender; titel: onderwerp; regel 3: doelgroep.
    override itemLines = 3;

    subject = messageListData.subject;
    sentOn = messageListData.sentOn;
    read = messageListData.read;
    mine = messageListData.mine;

    text = {
        context_separator: ' › ',
        separator: ' · ',
        audience_prefix: 'aan: ',
        audience_projectMembers: 'alle projectleden',
        audience_taskMembers: 'alle taakleden',
        unread: 'ongelezen',
        acknowledge: 'bevestiging gevraagd',
        acknowledged: 'bevestigd',
        read_count: 'gelezen',
        acknowledged_count: 'bevestigd',
    };

    context_text(item: IMessageListData | null): string {
        if (!item || this.context$$() === messageListContext.project || this.context$$() === messageListContext.task) return '';
        const __parts = this.context$$() === messageListContext.mine
            ? [item[messageListData.domain_name], item[messageListData.project_name], item[messageListData.task_subject]]
            : [item[messageListData.project_name], item[messageListData.task_subject]];
        return __parts.filter(x => !!x).join(this.text.context_separator);
    }

    sender_text(item: IMessageListData | null): string {
        if (!item) return '';
        const __org = item[messageListData.sender_organisation_label];
        return __org ? `${item[messageListData.sender_name] ?? ''}${this.text.separator}${__org}` : item[messageListData.sender_name] ?? '';
    }

    audience_text(item: IMessageListData | null): string {
        if (!item) return '';
        const __parts = [
            item[messageListData.audience_projectMembers] ? this.text.audience_projectMembers : null,
            item[messageListData.audience_taskMembers] ? this.text.audience_taskMembers : null,
            item[messageListData.audience_names],
        ].filter(x => !!x);
        return __parts.length ? `${this.text.audience_prefix}${__parts.join(', ')}` : '';
    }

    // Te bevestigen: ontvanger, bevestiging gevraagd, nog niet bevestigd.
    acknowledgePending(item: IMessageListData | null): boolean {
        return !!item && item[messageListData.isRecipient] && item[messageListData.acknowledgeRequired] && !item[messageListData.acknowledged];
    }

    // Afzender/beheer: "3/5 gelezen" (en bij bevestiging "2/5 bevestigd").
    counts_text(item: IMessageListData | null): string | null {
        const __total = item?.[messageListData.recipientCount];
        if (item == null || __total == null) return null;
        const __read = `${item[messageListData.readCount] ?? 0}/${__total} ${this.text.read_count}`;
        const __ack = item[messageListData.acknowledgedCount];
        return __ack == null ? __read : `${__read}${this.text.separator}${__ack}/${__total} ${this.text.acknowledged_count}`;
    }

    override ngOnInit(): void {
        this._ngOnInit();
    }

    // Verzenden: dialoog met doelgroep, onderwerp en tekst; daarna meteen het bericht openen.
    override emit_btnAddClick(): void {
        const __routeKeys = this._activatedRouteService.routeKeys;
        const __data: IMessageSendDialogData = {
            projectAMemberGuid: this.context$$() === messageListContext.project || this.context$$() === messageListContext.task
                ? __routeKeys?.pamGuid ?? undefined : undefined,
            domainAUserGuid: this.context$$() === messageListContext.domain ? __routeKeys?.dauGuid ?? undefined : undefined,
            taskGuid: this.context$$() === messageListContext.task ? __routeKeys?.key ?? undefined : undefined,
        };
        this.__matDialog
            // Injector van de pagina: controls in de dialoog kunnen pagina-services vragen (zie NoticePartyDialog).
            .open<MessageSendDialogComponent, IMessageSendDialogData, IMessageDto>(MessageSendDialogComponent, { data: __data, autoFocus: 'first-tabbable', injector: this.__injector })
            .afterClosed()
            .subscribe(dto => {
                if (!dto?.[messageDto.guid]) return;
                this._tabListService.tabList_setRefresh(true);
                this.__messagePageService.pageMessage_goto(dto[messageDto.guid]!, __routeKeys?.dauGuid ?? null);
            });
    }

    override emit_onClick(item: ILevelGeneralListData): void {
        this.__messagePageService.pageRoute_goto(item as IMessageListData);
    }

    // Berichten worden niet zacht verwijderd, dus ook niet teruggezet.
    override emit_onRestore(_restoreList: any[]): void {
    }

    protected override _list_get$(): Observable<IMessageListDto[]> {
        // Een lijst laden is ook een goed moment om de badge bij te werken (de tellers kunnen net veranderd zijn).
        this.__chatPollingService.refresh();

        const __routeKeys = this._activatedRouteService.routeKeys;
        const __request = (() => {
            switch (this.context$$()) {
                case messageListContext.mine:
                    return this.__apiService.GET('/api/Message/mine', {});
                case messageListContext.domain:
                    return this.__apiService.GET('/api/Message/list-from-domain', {
                        params: { query: { domainAUserGuid: __routeKeys?.dauGuid ?? undefined } }
                    });
                case messageListContext.task:
                    return this.__apiService.GET('/api/Message/list-from-task', {
                        params: { query: { key: __routeKeys?.key ?? undefined, projectAMemberGuid: __routeKeys?.pamGuid ?? undefined } }
                    });
                case messageListContext.function:
                    return this.__apiService.GET('/api/Message/list-from-function', {
                        params: { query: { key: __routeKeys?.key ?? undefined, domainAUserGuid: __routeKeys?.dauGuid ?? undefined } }
                    });
                default:
                    return this.__apiService.GET('/api/Message/list-from-project', {
                        params: { query: { projectAMemberGuid: __routeKeys?.pamGuid ?? undefined } }
                    });
            }
        })();
        return from(__request).pipe(map(r => (r.data as any)?.list as IMessageListDto[] ?? []));
    }

    protected override _restore_patch$(_restoreList: any[], _httpParams: HttpParams | null): Observable<IMessageListDto> {
        return EMPTY;
    }
}
